/** @jsxImportSource @emotion/react */
import moment from 'moment'
import DateTimePicker from 'components/atoms/DateTimePicker'
import Input from 'components/atoms/Input'
import { TimeWrapper } from 'components/lab/orderEdit/PsgHstWorkflow/FollowUpForm'
import React from 'react'
import { easternDay, formatEasternDate } from 'helpers/dates'
import { ChargeAccountTransaction } from 'store/types'

export type PaymentMethod = 'Card' | 'ACH' | 'Check' | 'Other'

// "Other" is for an entry no check, EFT or card stands behind (a non-payment,
// say): it asks for nothing beyond the payer claim number, so it is never
// recorded as a card just to get past the required fields.
export const PAYMENT_METHOD_OPTIONS: { key: PaymentMethod; value: string }[] = [
  { key: 'Check', value: 'Check' },
  { key: 'ACH', value: 'ACH' },
  { key: 'Card', value: 'Card' },
  { key: 'Other', value: 'Other' },
]

export const PAYMENT_METHODS = PAYMENT_METHOD_OPTIONS.map(({ key }) => key)

/**
 * What goes with how the payer sent the money. Every posting carries the
 * payer's claim control number (CLP07); the rest depends on the method:
 * check number and date, the EFT trace / settlement date / originating
 * company id, or what running a card produced.
 */
export type PaymentMethodDetailValues = {
  paymentMethod: PaymentMethod
  payerClaimControlNumber: string
  checkNumber: string
  checkDate: string
  eftTraceNumber: string
  effectiveDate: string
  originatingCompanyId: string
  cardTransactionId: string
  cardProcessedDate: string
  cardLastFour: string
}

export const emptyPaymentMethodDetails = (): Omit<PaymentMethodDetailValues, 'paymentMethod'> => ({
  payerClaimControlNumber: '',
  checkNumber: '',
  checkDate: '',
  eftTraceNumber: '',
  effectiveDate: '',
  originatingCompanyId: '',
  cardTransactionId: '',
  cardProcessedDate: '',
  cardLastFour: '',
})

/**
 * What a form without Formik checks before it posts — the same rules the
 * modals' Yup schemas apply: a check needs its number and date, an EFT its
 * trace number and settlement date, a card's last four must be four digits.
 */
export const validatePaymentMethodDetails = (values: PaymentMethodDetailValues): string | null => {
  if (values.paymentMethod === 'Check') {
    if (!values.checkNumber.trim()) return 'Check number is required'
    if (!values.checkDate) return 'Check date is required'
  }

  if (values.paymentMethod === 'ACH') {
    if (!values.eftTraceNumber.trim()) return 'EFT trace number is required'
    if (!values.effectiveDate) return 'Effective date is required'
  }

  if (values.paymentMethod === 'Card' && values.cardLastFour && !/^\d{4}$/.test(values.cardLastFour)) {
    return 'Card last four must be four digits'
  }

  return null
}

/** The request body for the details, with whatever the chosen method does not use left out. */
export const paymentMethodDetailsBody = (values: PaymentMethodDetailValues) => {
  const isCheck = values.paymentMethod === 'Check'
  const isAch = values.paymentMethod === 'ACH'
  const isCard = values.paymentMethod === 'Card'

  return {
    payerClaimControlNumber: values.payerClaimControlNumber?.trim() || undefined,
    checkNumber: isCheck ? values.checkNumber : undefined,
    checkDate: isCheck ? values.checkDate : undefined,
    eftTraceNumber: isAch ? values.eftTraceNumber : undefined,
    effectiveDate: isAch ? values.effectiveDate : undefined,
    originatingCompanyId: isAch ? values.originatingCompanyId : undefined,
    cardTransactionId: isCard ? values.cardTransactionId : undefined,
    cardProcessedDate: isCard ? values.cardProcessedDate : undefined,
    cardLastFour: isCard ? values.cardLastFour : undefined,
  }
}

export type PaymentDetailsDraft = PaymentMethodDetailValues & {
  /** YYYY-MM-DD */
  paymentDate: string
}

type PaymentDetails = NonNullable<ChargeAccountTransaction['paymentDetails']>

/**
 * A hand-entered payment's own posting row can correct the payment's details.
 * ERA postings carry the remittance's, and reversals, applied prepayments and
 * patient write-offs are not the posting.
 */
export const canEditPaymentDetails = (transaction: ChargeAccountTransaction): boolean =>
  (!transaction.type || transaction.type === 'SYSTEM_PAYMENT')
  && !transaction.reversesId
  && !transaction.eraClaimId
  && PAYMENT_METHOD_OPTIONS.some(({ key }) => key === transaction.paymentDetails?.paymentMethod)

export const paymentDetailsDraftOf = (details: PaymentDetails): PaymentDetailsDraft => ({
  paymentMethod: details.paymentMethod as PaymentMethod,
  paymentDate: easternDay(details.paymentDate),
  payerClaimControlNumber: details.payerClaimControlNumber ?? '',
  checkNumber: details.checkNumber ?? '',
  checkDate: details.checkDate ?? '',
  eftTraceNumber: details.eftTraceNumber ?? '',
  effectiveDate: details.effectiveDate ?? '',
  originatingCompanyId: details.originatingCompanyId ?? '',
  cardTransactionId: details.cardTransactionId ?? '',
  cardProcessedDate: details.cardProcessedDate ?? '',
  cardLastFour: details.cardLastFour ?? '',
})

/** The payment's method details as short display parts, e.g. ["#1234", "dated 09/22/2026"]. */
export const paymentDetailsSummary = (details: PaymentDetails): string[] => {
  const day = (value?: string | null) => (value ? formatEasternDate(value, '') : '')
  const parts = {
    Check: [
      details.checkNumber && `#${details.checkNumber}`,
      details.checkDate && `dated ${day(details.checkDate)}`,
    ],
    ACH: [
      details.eftTraceNumber && `EFT trace ${details.eftTraceNumber}`,
      details.effectiveDate && `effective ${day(details.effectiveDate)}`,
    ],
    Card: [
      details.cardLastFour && `card ending ${details.cardLastFour}`,
      details.cardTransactionId && `transaction ${details.cardTransactionId}`,
      details.cardProcessedDate && `processed ${day(details.cardProcessedDate)}`,
    ],
    Other: [],
  }[details.paymentMethod as PaymentMethod] ?? []

  return [
    ...parts,
    details.payerClaimControlNumber && `payer claim #${details.payerClaimControlNumber}`,
  ].filter((part): part is string => Boolean(part))
}

export const validatePaymentDetailsDraft =(draft: PaymentDetailsDraft): string | null =>
  draft.paymentDate ? validatePaymentMethodDetails(draft) : 'Payment date is required'

/** The request body for the fields the biller changed; undefined when none were. */
export const changedPaymentDetailsBody = (draft: PaymentDetailsDraft, original: PaymentDetailsDraft) => {
  const changed = (Object.keys(draft) as (keyof PaymentDetailsDraft)[]).filter((field) => draft[field] !== original[field])

  if (!changed.length) return undefined

  return Object.fromEntries(changed.map((field) => [
    field,
    field === 'paymentDate' ? new Date(`${draft.paymentDate}T00:00:00`).toISOString() : draft[field],
  ]))
}

type Layout = {
  /** Bootstrap column class of each field; the modals' default is a quarter row. */
  columnClassName: string
  /** Small controls and labels, for the inline posting row. */
  compact: boolean
}

type FieldProps = Layout & {
  label: string
  hint?: string
  disabled?: boolean
}

export const Field: React.FC<FieldProps & { children: React.ReactNode }> = ({
  label,
  hint,
  columnClassName,
  compact,
  children,
}) => (
  <div className={columnClassName}>
    <label className="textGray" css={compact ? { fontSize: '0.75rem', marginBottom: 2 } : undefined}>
      {label}
      {hint && <span css={{ color: '#adb5bd', fontSize: 11, marginLeft: 4 }}>{hint}</span>}
    </label>
    {children}
  </div>
)

const TextField: React.FC<FieldProps & {
  name: keyof PaymentMethodDetailValues
  value: string
  placeholder: string
  onChange: (value: string) => void
}> = ({ label, hint, name, value, placeholder, disabled, onChange, columnClassName, compact }) => (
  <Field label={label} hint={hint} columnClassName={columnClassName} compact={compact}>
    <Input
      value={value}
      name={name}
      placeholder={placeholder}
      disabled={disabled}
      handleChange={(e) => onChange(e.target.value)}
      className={compact ? 'form-control form-control-sm' : 'form-control'}
      parentDiv={null}
    />
  </Field>
)

export const DateField: React.FC<FieldProps & {
  value: string
  onChange: (value: string) => void
}> = ({ label, hint, value, disabled, onChange, columnClassName, compact }) => (
  <Field label={label} hint={hint} columnClassName={columnClassName} compact={compact}>
    <TimeWrapper>
      <DateTimePicker
        value={value ? moment(value).toDate() : null}
        handleChange={(d) => onChange(d ? moment(d).format('YYYY-MM-DD') : '')}
        showTimeSelect={false}
        isClearable
        disabled={disabled}
        className={compact ? 'form-control form-control-sm' : 'form-control'}
        dateFormat="MMMM d, yyyy"
      />
    </TimeWrapper>
  </Field>
)

type Props = {
  values: PaymentMethodDetailValues
  setFieldValue: (field: string, value: string) => void
  disabled?: boolean
  columnClassName?: string
  compact?: boolean
}

/**
 * The inputs that go with the chosen payment method. Renders as columns of
 * the surrounding bootstrap row, right after the Payment Method select.
 */
export const PaymentMethodDetailsFields: React.FC<Props> = ({
  values,
  setFieldValue,
  disabled,
  columnClassName = 'col-6 col-md-3',
  compact = false,
}) => {
  const set = (field: keyof PaymentMethodDetailValues) => (value: string) => setFieldValue(field, value)
  const layout = { columnClassName, compact }

  return (
    <>
      <TextField
        {...layout}
        label="Claim Number"
        hint="CLP07"
        name="payerClaimControlNumber"
        value={values.payerClaimControlNumber}
        placeholder="Payer's claim number"
        disabled={disabled}
        onChange={set('payerClaimControlNumber')}
      />
      {values.paymentMethod === 'Check' && (
        <>
          <TextField
            {...layout}
            label="Check number"
            name="checkNumber"
            value={values.checkNumber}
            placeholder="Enter check number"
            disabled={disabled}
            onChange={set('checkNumber')}
          />
          <DateField {...layout} label="Check date" value={values.checkDate} disabled={disabled} onChange={set('checkDate')} />
        </>
      )}
      {values.paymentMethod === 'ACH' && (
        <>
          <TextField
            {...layout}
            label="EFT trace number"
            hint="TRN02"
            name="eftTraceNumber"
            value={values.eftTraceNumber}
            placeholder="Enter EFT trace number"
            disabled={disabled}
            onChange={set('eftTraceNumber')}
          />
          <DateField
            {...layout}
            label="Effective date"
            hint="settles on"
            value={values.effectiveDate}
            disabled={disabled}
            onChange={set('effectiveDate')}
          />
          <TextField
            {...layout}
            label="Originating company ID"
            hint="BPR10"
            name="originatingCompanyId"
            value={values.originatingCompanyId}
            placeholder="Payer's originating company id"
            disabled={disabled}
            onChange={set('originatingCompanyId')}
          />
        </>
      )}
      {values.paymentMethod === 'Card' && (
        <>
          <TextField
            {...layout}
            label="Transaction / auth #"
            name="cardTransactionId"
            value={values.cardTransactionId}
            placeholder="Transaction, authorization or confirmation #"
            disabled={disabled}
            onChange={set('cardTransactionId')}
          />
          <DateField
            {...layout}
            label="Processed date"
            value={values.cardProcessedDate}
            disabled={disabled}
            onChange={set('cardProcessedDate')}
          />
          <TextField
            {...layout}
            label="Card last four"
            name="cardLastFour"
            value={values.cardLastFour}
            placeholder="1234"
            disabled={disabled}
            onChange={(value) => set('cardLastFour')(value.replace(/\D/g, '').slice(0, 4))}
          />
        </>
      )}
    </>
  )
}
